import { AlertBannerType, ExAlertBanner } from '@boomi/exosphere';
import classNames from 'classnames';
import { pathOr } from 'ramda';
import { type ReactNode, useEffect, useState } from 'react';
import '../../../../css/tenant-select.less';
import { getUser } from '../../sources/user';
import translations from '../../translations';
import type { UserTenantResponseAPI } from '../../types/user';
import { getDateDisplay } from '../../utils/date';
import { isNullOrEmpty } from '../../utils/guard';
import { stringContains } from '../../utils/string';
import CopyableText from '../generic/CopyableText';
import TenantName from '../generic/TenantName';
import Modal from '../generic/modal/GenericModal';
import Loader from '../loader/Loader';
import { useNotificationDispatch } from '../notifications/NotificationProvider';
import { useUser } from '../UserProvider';
import { reloadPage } from '../../utils/browser';

export const matchesSearchQuery = (tenant: UserTenantResponseAPI, query: string) => {
    const organizationName = pathOr(null, ['organization', 'name'], tenant);
    const searchValues = [
        tenant.developerName,
        tenant.developerSummary,
        tenant.id,
        organizationName,
    ];

    return searchValues.some((val) => stringContains(val, query, false));
};

const switchTenant = async (tenantId: string, onTenantNotFound: () => void) => {
    const user = await getUser();
    const tenant = user.tenants.find((t) => t.id === tenantId);

    if (!tenant && tenantId) {
        onTenantNotFound();
        return;
    }

    if (tenant) {
        reloadPage(`/${tenant.id}/flows`);
    }
};

interface TenantSwitcherProps {
    onClose: () => void;
    currentTenant: UserTenantResponseAPI | null;
}

const emails = {
    support: 'support@boomi.com',
};

const TenantSwitcher = ({ onClose, currentTenant }: TenantSwitcherProps) => {
    const user = useUser();

    const dispatchNotification = useNotificationDispatch();

    const [search, setSearch] = useState('');
    const [tenantsToDisplay, setTenantsToDisplay] = useState<UserTenantResponseAPI[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchTenants = async () => {
            try {
                const user = await getUser();
                setTenantsToDisplay(user.tenants);
                setIsLoading(false);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
            }
        };
        fetchTenants();
    }, [dispatchNotification]);

    const onClick = (tenantId: string) => {
        switchTenant(tenantId, () =>
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error: `You are not a member of the tenant with id ${tenantId}`,
                },
            }),
        );
    };

    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    };

    const tenants = tenantsToDisplay
        .filter((tenant) => matchesSearchQuery(tenant, search))
        .map((listedTenant) => {
            const lastLoggedInAt =
                listedTenant.lastLoggedInAt === null
                    ? 'Never'
                    : getDateDisplay(listedTenant.lastLoggedInAt);

            const expiresAt =
                listedTenant.expiresAt === null ? '' : getDateDisplay(listedTenant.expiresAt);

            const isActiveTenant = listedTenant.id === currentTenant?.id;

            const className = classNames('list-group-item', {
                active: isActiveTenant,
            });

            return (
                <button
                    type="button"
                    className={className}
                    onClick={() => onClick(listedTenant.id)}
                    key={listedTenant.id}
                    disabled={listedTenant.isExpired}
                >
                    <h4 className="list-group-item-heading">
                        <TenantName name={listedTenant.developerName} />
                    </h4>
                    <div className="tenant-id">{listedTenant.id}</div>
                    {listedTenant.organization ? (
                        <p className="list-group-item-text">{listedTenant.organization.name}</p>
                    ) : null}
                    <p className="list-group-item-text">{listedTenant.developerSummary}</p>
                    {isNullOrEmpty(expiresAt) ? null : (
                        <p className="list-group-item-text margin-top-small tenant-last-login">
                            {listedTenant.isExpired
                                ? `Your trial expired on ${expiresAt} and has been deactivated.`
                                : `Tenant Expires: ${expiresAt}`}
                        </p>
                    )}
                    <p className="list-group-item-text tenant-last-login">{`Last Login: ${lastLoggedInAt}`}</p>
                </button>
            );
        });

    let tenantList: ReactNode = null;
    if (tenants.length) {
        tenantList = (
            <div className="list-group" data-testid="tenant-switcher-list">
                {tenants}
            </div>
        );
    } else if (!tenants.length && search) {
        tenantList = (
            <ExAlertBanner className="search-empty" type={AlertBannerType.WARNING} open>
                {translations.TENANT_switcher_no_matching_tenants_message}
            </ExAlertBanner>
        );
    } else {
        tenantList = (
            <ExAlertBanner className="tenants-empty" type={AlertBannerType.WARNING} open>
                <div>
                    {translations.TENANT_switcher_no_tenants_message_1}
                    <br />
                    <br />
                    {translations.TENANT_switcher_no_tenants_message_2}
                    <b>
                        <CopyableText copyableText={user.id} hasCopyButton={true} />
                    </b>
                    <br />
                    <br />
                    {translations.TENANT_switcher_no_tenants_message_3}
                    <b>
                        <a href={`mailto:${emails.support}`}>{emails.support}</a>
                    </b>
                </div>
            </ExAlertBanner>
        );
    }

    // Only display this SSO information box if we're logged in using SSO credentials
    let ssoAlert: ReactNode = null;
    if (currentTenant?.isSso) {
        ssoAlert = (
            <ExAlertBanner type={AlertBannerType.INFO} open>
                <div>
                    <strong>Note:</strong>
                    {'\n'}
                    {translations.TENANT_switcher_sso_alert_message}
                </div>
            </ExAlertBanner>
        );
    }

    const content = isLoading ? (
        <Loader />
    ) : (
        <div>
            <input
                className="form-control"
                value={search}
                onChange={onSearchChange}
                placeholder="Search"
            />
            {ssoAlert}
            <div className="overflow-y-auto">{tenantList}</div>
        </div>
    );

    return (
        <Modal
            show
            onHide={() => {
                if (isNullOrEmpty(currentTenant)) {
                    dispatchNotification({
                        type: 'addNotification',
                        payload: {
                            type: 'info',
                            message: 'Please select a tenant',
                            isPersistent: false,
                        },
                    });
                    return;
                }

                onClose();
            }} // Only show close button if you're currently logged into a tenant
            dialogClassName="tenant-select"
            title="Select a Tenant"
            renderBody={() => content}
        />
    );
};

export default TenantSwitcher;
